/**
 * L2 drill-down: every backup job, from every source, filterable by source,
 * site, SLA tier and outcome — all held in the URL so a filtered view is
 * shareable and the back button walks back up the drill path (design rules
 * §1 / §4.2).
 *
 * This is the level where a full table belongs. The L1 board shows at most ten
 * jobs needing attention; this page shows all of them, and a job name opens the
 * drawer with its run history and the acknowledgement form.
 */

import {
  Alert,
  type BreadcrumbItem,
  Breadcrumbs,
  DataGrid,
  EmptyState,
  Freshness,
  PageHeader,
  Skeleton,
  Typography,
} from "@norican/react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { FilterRow } from "@/components/board";
import { type BackupJobRow, useBackupJobs } from "@/lib/api";
import { formatCount } from "@/lib/charts/config";
import { useDrawerParam, useDrillParams } from "@/lib/drill";

import { jobColumns } from "./job-columns";
import { JobDrawer } from "./job-drawer";
import { PanelCard } from "./panel-card";
import {
  JOB_DRAWER_PARAM,
  parseJobKey,
  RESULT_PARAM,
  SITE_PARAM,
  SLA_PARAM,
  SOURCE_PARAM,
} from "./params";
import { sourceLabel, STATUS_ORDER, statusStyle } from "./status";

const BREADCRUMBS: BreadcrumbItem[] = [
  {
    label: "Backup",
    render: ({ className, children }: { className: string; children: ReactNode }) => (
      <Link to="/backup" className={className}>
        {children}
      </Link>
    ),
  },
  { label: "Jobs", current: true },
];

export function BackupJobsPage() {
  const jobs = useBackupJobs();
  const drill = useDrillParams(SOURCE_PARAM, SITE_PARAM, SLA_PARAM, RESULT_PARAM);
  const drawer = useDrawerParam(JOB_DRAWER_PARAM);

  const data = jobs.data;
  const sources = drill.getList(SOURCE_PARAM);
  const sites = drill.getList(SITE_PARAM);
  const slas = drill.getList(SLA_PARAM);
  const results = drill.getList(RESULT_PARAM);

  const all = data?.jobs ?? [];
  const rows = all.filter(
    (job) =>
      (sources.length === 0 || sources.includes(job.source)) &&
      (sites.length === 0 || (job.site != null && sites.includes(job.site))) &&
      (slas.length === 0 || (job.sla != null && slas.includes(job.sla))) &&
      (results.length === 0 || results.includes(job.status)),
  );

  const open = parseJobKey(drawer.value);

  // Filter options come from the FULL list, never the filtered slice, so
  // narrowing to one site cannot make the other sites unpickable.
  const sourceOptions = [...new Set(all.map((job) => job.source))].map((source) => ({
    value: source,
    label: sourceLabel(source),
  }));
  const siteOptions = [
    ...new Set(all.map((job) => job.site).filter((site): site is string => !!site)),
  ]
    .sort()
    .map((site) => ({ value: site, label: site }));
  const slaOptions = [...new Set(all.map((job) => job.sla).filter((sla): sla is string => !!sla))]
    .sort()
    .map((sla) => ({ value: sla, label: sla }));

  const drillParams = {
    ...(sources.length > 0 ? { [SOURCE_PARAM]: sources } : {}),
    ...(sites.length > 0 ? { [SITE_PARAM]: sites } : {}),
    ...(slas.length > 0 ? { [SLA_PARAM]: slas } : {}),
    ...(results.length > 0 ? { [RESULT_PARAM]: results } : {}),
  };

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <Breadcrumbs items={BREADCRUMBS} />
      <PageHeader
        title="Backup jobs"
        subtitle="Every job from every protection source, with its latest outcome and the acknowledgements against it."
        actions={
          data?.collected_at ? <Freshness label="Collected" since={data.collected_at} /> : undefined
        }
      />

      {jobs.error && (
        <Alert tone="danger" title="Couldn't load the job list">
          {jobs.error instanceof Error ? jobs.error.message : "The NOVA API did not respond."}
        </Alert>
      )}

      {jobs.isPending && !data ? (
        <Skeleton height="24rem" />
      ) : (
        data &&
        (!data.configured ? (
          <PanelCard title="Jobs" caption="Not available">
            <Typography variant="body-sm" tone="muted">
              {data.status_detail}
            </Typography>
          </PanelCard>
        ) : (
          <>
            <FilterRow
              showRange={false}
              dimensions={[
                {
                  param: SOURCE_PARAM,
                  label: "Source",
                  noun: "source",
                  options: sourceOptions,
                },
                { param: SITE_PARAM, label: "Site", noun: "site", options: siteOptions },
                { param: SLA_PARAM, label: "SLA tier", noun: "tier", options: slaOptions },
                {
                  param: RESULT_PARAM,
                  label: "Result",
                  noun: "result",
                  options: STATUS_ORDER.map((status) => ({
                    value: status,
                    label: statusStyle(status).label,
                  })),
                },
              ]}
            />

            <PanelCard
              title="Jobs"
              caption={
                rows.length === all.length
                  ? `${formatCount(all.length)} jobs`
                  : `${formatCount(rows.length)} of ${formatCount(all.length)} jobs match`
              }
              padding={rows.length === 0 ? "md" : "none"}
            >
              {rows.length === 0 ? (
                <EmptyState
                  size="sm"
                  title="No jobs match these filters"
                  description="Clear a filter above to widen the view."
                />
              ) : (
                <DataGrid<BackupJobRow>
                  data={rows}
                  getRowId={(job) => `${job.source}:${job.name}`}
                  density="compact"
                  aria-label="Every backup job in scope"
                  getRowSearchText={(job) =>
                    [job.name, job.site, job.sla, job.job_type, sourceLabel(job.source)]
                      .filter(Boolean)
                      .join(" ")
                  }
                  columns={jobColumns({ drillParams, detailed: true })}
                />
              )}
              {data.errors.length > 0 && (
                <Typography variant="body-sm" tone="muted">
                  Collection problems: {data.errors.join(" · ")}
                </Typography>
              )}
            </PanelCard>
          </>
        ))
      )}

      <JobDrawer name={open?.name ?? null} source={open?.source ?? null} onClose={drawer.close} />
    </main>
  );
}
